// Google Analytics 4 Ecommerce Events
// Documentation: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce

import {
  trackMetaAddShippingInfo,
  trackMetaAddToCart,
  trackMetaAddPaymentInfo,
  trackMetaInitiateCheckout,
  trackMetaPurchase,
  trackMetaRemoveFromCart,
  trackMetaViewCart,
  trackMetaViewContent,
  trackMetaViewItemList,
} from './metaConversions';

/**
 * Helper to ensure GTM dataLayer is available
 */
const getDataLayer = () => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }
  return null;
};

/**
 * Helper to format product items for GA4
 */
const formatProductItem = (product, index = 0) => {
  return {
    item_id: product.id || product.sku,
    item_name: product.name || product.title,
    affiliation: "Felicita Fragrances",
    currency: "TRY",
    discount: product.discount || 0,
    index: index,
    item_brand: product.brand || "Felicita",
    item_category: product.category || "",
    item_category2: product.subcategory || "",
    item_variant: product.variant || "",
    price: product.unitPrice || product.price || 0,
    quantity: product.quantity || 1
  };
};

/**
 * Track add to cart event
 * @param {Object} product - Product object with id, name, price, quantity, etc.
 * @param {number} value - Total value of items added
 */
export const trackAddToCart = (product, value, userData = {}) => {
  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  try {
    const eventData = {
      event: 'add_to_cart',
      meta_pixel_handled: true,
      ecommerce: {
        currency: 'TRY',
        value: value || (product.price * (product.quantity || 1)),
        items: [formatProductItem(product)]
      }
    };

    dataLayer.push({ ecommerce: null }); // Clear previous ecommerce data
    dataLayer.push(eventData);

    trackMetaAddToCart(product, userData);
    
    console.log('GA4 Event: add_to_cart', eventData);
  } catch (error) {
    console.error('Error tracking add_to_cart:', error);
  }
};

/**
 * Track remove from cart event
 * @param {Object} product - Product object
 * @param {number} value - Total value of items removed
 */
export const trackRemoveFromCart = (product, value) => {
  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  try {
    const eventData = {
      event: 'remove_from_cart',
      meta_pixel_handled: true,
      ecommerce: {
        currency: 'TRY',
        value: value || (product.price * (product.quantity || 1)),
        items: [formatProductItem(product)]
      }
    };

    dataLayer.push({ ecommerce: null });
    dataLayer.push(eventData);

    trackMetaRemoveFromCart(product, value);

    console.log('GA4 Event: remove_from_cart', eventData);
  } catch (error) {
    console.error('Error tracking remove_from_cart:', error);
  }
};

/**
 * Track view cart event
 * @param {Array} cartItems - Array of cart items
 * @param {number} value - Total cart value
 */
export const trackViewCart = (cartItems, value) => {
  const dataLayer = getDataLayer();
  if (!dataLayer || !cartItems || cartItems.length === 0) return;

  try {
    const eventData = {
      event: 'view_cart',
      meta_pixel_handled: true,
      ecommerce: {
        currency: 'TRY',
        value: value,
        items: cartItems.map((item, index) => formatProductItem(item, index))
      }
    };

    dataLayer.push({ ecommerce: null });
    dataLayer.push(eventData);

    trackMetaViewCart(cartItems, value);
    
    console.log('GA4 Event: view_cart', eventData);
  } catch (error) {
    console.error('Error tracking view_cart:', error);
  }
};

/**
 * Track begin checkout event
 * @param {Array} cartItems - Array of cart items
 * @param {number} value - Total cart value
 * @param {string} coupon - Applied coupon code (optional)
 */
export const trackBeginCheckout = (cartItems, value, coupon = null, userData = {}) => {
  const dataLayer = getDataLayer();
  if (!dataLayer || !cartItems || cartItems.length === 0) return;

  try {
    const eventData = {
      event: 'begin_checkout',
      meta_pixel_handled: true,
      ecommerce: {
        currency: 'TRY',
        value: value,
        items: cartItems.map((item, index) => formatProductItem(item, index))
      }
    };

    if (coupon) {
      eventData.ecommerce.coupon = coupon;
    }

    dataLayer.push({ ecommerce: null });
    dataLayer.push(eventData);

    trackMetaInitiateCheckout(cartItems, value, userData);
    
    console.log('GA4 Event: begin_checkout', eventData);
  } catch (error) {
    console.error('Error tracking begin_checkout:', error);
  }
};

/**
 * Track add shipping info event
 * @param {Array} cartItems - Array of cart items
 * @param {number} value - Total cart value
 * @param {string} shippingTier - Shipping method selected
 */
export const trackAddShippingInfo = (cartItems, value, shippingTier) => {
  const dataLayer = getDataLayer();
  if (!dataLayer || !cartItems) return;

  try {
    const eventData = {
      event: 'add_shipping_info',
      meta_pixel_handled: true,
      ecommerce: {
        currency: 'TRY',
        value: value,
        shipping_tier: shippingTier,
        items: cartItems.map((item, index) => formatProductItem(item, index))
      }
    };

    dataLayer.push({ ecommerce: null });
    dataLayer.push(eventData);

    trackMetaAddShippingInfo(cartItems, value, shippingTier);

    console.log('GA4 Event: add_shipping_info', eventData);
  } catch (error) {
    console.error('Error tracking add_shipping_info:', error);
  }
};

/**
 * Track add payment info event
 * @param {Array} cartItems - Array of cart items
 * @param {number} value - Total cart value
 * @param {string} paymentType - Payment method selected
 */
export const trackAddPaymentInfo = (cartItems, value, paymentType) => {
  const dataLayer = getDataLayer();
  if (!dataLayer || !cartItems) return;

  try {
    const eventData = {
      event: 'add_payment_info',
      meta_pixel_handled: true,
      ecommerce: {
        currency: 'TRY',
        value: value,
        payment_type: paymentType,
        items: cartItems.map((item, index) => formatProductItem(item, index))
      }
    };

    dataLayer.push({ ecommerce: null });
    dataLayer.push(eventData);

    trackMetaAddPaymentInfo(cartItems, value);
    
    console.log('GA4 Event: add_payment_info', eventData);
  } catch (error) {
    console.error('Error tracking add_payment_info:', error);
  }
};

/**
 * Track purchase event
 * @param {Object} orderData - Order details including transaction_id, value, items, etc.
 */
export const trackPurchase = (orderData) => {
  const dataLayer = getDataLayer();
  if (!dataLayer || !orderData) return;

  try {
    const transactionId = orderData.transactionId || orderData.orderId || '';
    const value = orderData.value || orderData.total || 0;
    
    // Clear previous ecommerce data
    dataLayer.push({ ecommerce: null });
    
    // GA4 Purchase Event for GTM
    const items = orderData.items ? orderData.items.map((item, index) => formatProductItem(item, index)) : [];
    const eventData = {
      event: 'purchase',
      meta_pixel_handled: true,
      ecommerce: {
        transaction_id: transactionId,
        value: value,
        tax: orderData.tax || 0,
        shipping: orderData.shipping || 0,
        currency: 'TRY',
        coupon: orderData.coupon || '',
        items,
      }
    };
    
    dataLayer.push(eventData);

    trackMetaPurchase({
      orderId: transactionId,
      value,
      items: orderData.items || [],
      userData: {
        email: orderData.customerEmail || null,
        phone: orderData.customerPhone || null,
        externalId: orderData.userId || orderData.customerEmail || transactionId,
      },
    });

    console.log('GTM Purchase Event pushed to dataLayer:', eventData);
    
    // Additional data for conversion tracking (can be used in GTM triggers)
    dataLayer.push({
      event: 'conversion',
      conversion_data: {
        transaction_id: transactionId,
        value: value,
        currency: 'TRY'
      }
    });
    
  } catch (error) {
    console.error('Error tracking purchase:', error);
  }
};

/**
 * Track view item event
 * @param {Object} product - Product being viewed
 */
export const trackViewItem = (product) => {
  const dataLayer = getDataLayer();
  if (!dataLayer || !product) return;

  try {
    const eventData = {
      event: 'view_item',
      meta_pixel_handled: true,
      ecommerce: {
        currency: 'TRY',
        value: product.price || 0,
        items: [formatProductItem(product)]
      }
    };

    dataLayer.push({ ecommerce: null });
    dataLayer.push(eventData);

    trackMetaViewContent(product);
    
    console.log('GA4 Event: view_item', eventData);
  } catch (error) {
    console.error('Error tracking view_item:', error);
  }
};

/**
 * Track view item list event
 * @param {Array} items - Array of products in the list
 * @param {string} listName - Name of the list (e.g., "Search Results", "Category Page")
 */
export const trackViewItemList = (items, listName = "Product List") => {
  const dataLayer = getDataLayer();
  if (!dataLayer || !items || items.length === 0) return;

  try {
    const eventData = {
      event: 'view_item_list',
      meta_pixel_handled: true,
      ecommerce: {
        item_list_name: listName,
        items: items.map((item, index) => ({
          ...formatProductItem(item, index),
          item_list_name: listName
        }))
      }
    };

    dataLayer.push({ ecommerce: null });
    dataLayer.push(eventData);

    trackMetaViewItemList(items, listName);
    
    console.log('GA4 Event: view_item_list', eventData);
  } catch (error) {
    console.error('Error tracking view_item_list:', error);
  }
};

/**
 * Track select item event
 * @param {Object} product - Product that was selected
 * @param {string} listName - Name of the list where selection occurred
 */
export const trackSelectItem = (product, listName = "Product List") => {
  const dataLayer = getDataLayer();
  if (!dataLayer || !product) return;

  try {
    const eventData = {
      event: 'select_item',
      meta_pixel_handled: true,
      ecommerce: {
        item_list_name: listName,
        items: [{
          ...formatProductItem(product),
          item_list_name: listName
        }]
      }
    };

    dataLayer.push({ ecommerce: null });
    dataLayer.push(eventData);

    console.log('GA4 Event: select_item', eventData);
  } catch (error) {
    console.error('Error tracking select_item:', error);
  }
};

/**
 * Track WhatsApp chat open event
 */
export const trackWhatsAppOpen = () => {
  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  try {
    const eventData = {
      event: 'whatsapp_chat_open',
      meta_pixel_handled: true,
      event_category: 'engagement',
      event_label: 'WhatsApp Chat Widget Opened'
    };

    dataLayer.push(eventData);
    console.log('GA4 Event: whatsapp_chat_open', eventData);
  } catch (error) {
    console.error('Error tracking whatsapp_chat_open:', error);
  }
};

/**
 * Track WhatsApp message sent event
 * @param {string} message - The message content (optional, for debugging only - don't send PII)
 */
export const trackWhatsAppMessageSent = (messageLength) => {
  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  try {
    const eventData = {
      event: 'whatsapp_message_sent',
      meta_pixel_handled: true,
      event_category: 'engagement',
      event_label: 'WhatsApp Message Sent',
      message_length: messageLength || 0
    };

    dataLayer.push(eventData);
    console.log('GA4 Event: whatsapp_message_sent', eventData);
  } catch (error) {
    console.error('Error tracking whatsapp_message_sent:', error);
  }
};